import { useState } from "react";

export function FilterSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="border-b border-border py-2.5 first:pt-0 last:border-b-0">
      <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      {children}
    </div>
  );
}

export function ChipToggle({
  active,
  onToggle,
  children,
}: {
  active: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition ${
        active
          ? "border-foreground/40 bg-foreground text-background"
          : "border-border bg-card text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

export function ChipRow({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-wrap gap-1.5">{children}</div>;
}

// A single minimum-level select (grade / rating). Used for both Competency and
// Constraint thresholds — a resource matches when it holds every selected item at
// or above this level.
export function LevelSelect({
  value,
  min,
  max,
  onChange,
  label,
}: {
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
  label: string;
}) {
  const levels = Array.from({ length: max - min + 1 }, (_, i) => min + i);
  return (
    <label className="flex items-center gap-2 text-[12px] text-muted-foreground">
      <span>{label}</span>
      <select
        className="h-7 rounded-md border border-border bg-card px-2 text-[12px] outline-none"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      >
        {levels.map((l) => (
          <option key={l} value={l}>
            {l}+
          </option>
        ))}
      </select>
    </label>
  );
}

// Load range as two bounded number inputs. Floor defaults to 0; the ceiling is
// data-derived (max observed points) and an empty ceiling means unbounded.
export function PointsRange({
  min,
  max,
  ceiling,
  onChange,
}: {
  min: number;
  max: number | null;
  ceiling: number;
  onChange: (min: number, max: number | null) => void;
}) {
  const inputCls =
    "h-7 w-16 rounded-md border border-border bg-card px-2 text-[12px] tabular-nums outline-none focus:border-foreground/30";
  return (
    <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
      <input
        type="number"
        min={0}
        max={ceiling}
        value={min}
        onChange={(e) => {
          const lo = Math.max(0, Math.floor(Number(e.target.value) || 0));
          onChange(lo, max != null ? Math.max(lo, max) : null);
        }}
        className={inputCls}
        aria-label="Minimum points"
      />
      <span>to</span>
      <input
        type="number"
        min={0}
        max={ceiling}
        value={max ?? ""}
        placeholder={`${ceiling}`}
        onChange={(e) => {
          const raw = e.target.value.trim();
          if (raw === "") return onChange(min, null);
          const hi = Math.max(0, Math.floor(Number(raw) || 0));
          onChange(Math.min(min, hi), hi);
        }}
        className={inputCls}
        aria-label="Maximum points"
      />
      <span className="text-[11px]">pts</span>
    </div>
  );
}

export function SearchableMultiSelect({
  options,
  selected,
  onToggle,
  placeholder,
}: {
  options: Array<{ id: string; name: string }>;
  selected: string[];
  onToggle: (id: string) => void;
  placeholder: string;
}) {
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();
  const shown = query ? options.filter((o) => o.name.toLowerCase().includes(query)) : options;
  return (
    <div>
      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={placeholder}
        className="mb-1.5 h-7 w-full rounded-md border border-border bg-card px-2 text-[12px] outline-none focus:border-foreground/30"
      />
      <div className="max-h-40 overflow-y-auto">
        {shown.length === 0 && (
          <div className="px-1 py-2 text-[11px] text-muted-foreground">No matches.</div>
        )}
        {shown.map((o) => {
          const on = selected.includes(o.id);
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => onToggle(o.id)}
              className={`flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-[12px] transition hover:bg-muted/60 ${
                on ? "text-foreground" : "text-muted-foreground"
              }`}
            >
              <span
                className={`flex h-3.5 w-3.5 items-center justify-center rounded border text-[9px] ${
                  on ? "border-foreground bg-foreground text-background" : "border-border"
                }`}
              >
                {on ? "✓" : ""}
              </span>
              <span className="truncate">{o.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
